import { toFiniteNumber } from "./formValue";
import { layoutDesignChanged } from "./physicalLayoutDesign";

// The clear gap the initial roof grid leaves between lines of modules.
export const ROOF_LINE_GAP_M = 0.02;

export function roofLayoutSource(source, installationType) {
  if (installationType !== "roof_flush" && installationType !== "roof_frame") return source;
  const generated = source?.mode === "auto" ||
    (source?.source === "autodesign" && source?.mode !== "manual");
  // An explicitly saved manual layout or individual edits must survive.
  if (!generated || source?.module_array || Object.keys(source?.module_offsets || {}).length) return source;
  // Old automatic ground layouts are not roof placements. Retain the actual
  // site and its origin, but discard ALL generated row/parcel/spacing aliases.
  const retained = {};
  for (const key of ["site_geometry", "ground_clearance_m", "array_offset_east_m", "array_offset_north_m"]) {
    if (source[key] != null) retained[key] = source[key];
  }
  return retained;
}

export function hasSavedRoofLayout(source, totalModules, designKey) {
  const spacing = roofLayoutSource(source, "roof_flush");
  if (layoutDesignChanged(spacing, totalModules, designKey)) return false;
  return toFiniteNumber(spacing?.minimum_distance_m ?? spacing?.pitch_m ?? spacing?.pitch) > 0 &&
    toFiniteNumber(spacing?.row_vertical_modules ?? spacing?.rowVerticalModules) > 0 &&
    (toFiniteNumber(spacing?.field_rows ?? spacing?.fieldRows) > 0 ||
      (Array.isArray(spacing?.row_modules) && spacing.row_modules.some((count) => toFiniteNumber(count) > 0)));
}

// A compact starting arrangement only. No site fitting or shade-spacing rule
// is applied; persisted geometry and individual positions always take priority.
export function roofInitialGrid(source, { installationType, moduleLength, moduleWidth, portrait, tilt }) {
  if (installationType !== "roof_flush" && installationType !== "roof_frame") return source;
  const spacing = roofLayoutSource(source, installationType) || {};
  const alongSlope = toFiniteNumber(portrait === false ? moduleWidth : moduleLength);
  if (!(alongSlope > 0)) return spacing;
  const tiers = toFiniteNumber(spacing.row_vertical_modules ?? spacing.rowVerticalModules) ?? 1;
  const projection = installationType === "roof_flush"
    ? Math.cos((toFiniteNumber(tilt) ?? 0) * Math.PI / 180) : 1;
  return {
    ...spacing,
    row_vertical_modules: tiers,
    minimum_distance_m: toFiniteNumber(spacing.minimum_distance_m ?? spacing.pitch_m ?? spacing.pitch)
      ?? (alongSlope * tiers + ROOF_LINE_GAP_M) * projection,
  };
}


/**
 * Row spacing that still fits the module the roof layout holds.
 *
 * A saved roof layout keeps its spacing on purpose: it is the engineer's. But
 * the spacing is a distance, and it was a distance for ONE module. When the
 * module changes - an auto-design that picks a 2384 mm panel for a layout
 * drawn with 2278 mm ones - the old spacing laid the new modules 86 mm over
 * each other on the roof, and the page reported a gap of -8.6 cm.
 *
 * Flush on the roof the lines keep their CLEAR GAP (the default 2 cm when the
 * old one is unknown or already negative): that gap is what the installer
 * sets out. On a frame the rows keep their ground coverage ratio, so the
 * shade angle between rows is what it was. Unchanged modules with a valid gap
 * come back untouched; a flush layout that overlaps is healed to the default.
 */
export function fitRoofRowSpacing(spacing, { installationType, alongSlope, previousAlongSlope = null, tilt }) {
  if (installationType !== "roof_flush" && installationType !== "roof_frame") return spacing;
  const length = toFiniteNumber(alongSlope);
  const rawPitch = spacing?.minimum_distance_m ?? spacing?.pitch_m ?? spacing?.pitch;
  const pitch = toFiniteNumber(rawPitch);
  if (!(length > 0) || !(pitch > 0)) return spacing;
  const tiers = toFiniteNumber(spacing.row_vertical_modules ?? spacing.rowVerticalModules) || 1;
  const previous = toFiniteNumber(previousAlongSlope);
  const array = spacing.module_array;
  const arraySpacing = Number.isFinite(array?.rowSpacingM) ? array.rowSpacingM : null;
  const asStored = (value) => (typeof rawPitch === "string" ? String(value) : value);
  if (installationType === "roof_flush") {
    const cos = Math.cos((toFiniteNumber(tilt) ?? 0) * Math.PI / 180);
    if (!(cos > 1e-6)) return spacing;
    const along = arraySpacing != null ? Math.abs(arraySpacing) : pitch / cos;
    const oldGap = along - (previous > 0 ? previous : length) * tiers;
    const gap = oldGap >= -1e-9 ? Math.max(0, oldGap) : ROOF_LINE_GAP_M;
    const nextAlong = length * tiers + gap;
    if (Math.abs(nextAlong - along) < 1e-9) return spacing;
    return { ...spacing, minimum_distance_m: asStored(nextAlong * cos),
      ...(arraySpacing != null ? { module_array: { ...array, rowSpacingM: (arraySpacing < 0 ? -1 : 1) * nextAlong } } : {}) };
  }
  if (!(previous > 0) || Math.abs(previous - length) < 1e-9) return spacing;
  const factor = length / previous;
  return { ...spacing, minimum_distance_m: asStored(pitch * factor),
    ...(arraySpacing != null ? { module_array: { ...array, rowSpacingM: arraySpacing * factor } } : {}) };
}
